import { Heart, Sparkles, AlertTriangle, HelpCircle, Target, CircleDot, Scale, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { Vow, VowCategory } from "@/data/vows";
import { VOW_CATEGORIES } from "@/data/vows";

interface VowDetailDialogProps {
  vow: Vow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isFavorite: boolean;
  onToggleFavorite: (vowId: number) => void;
}

const CATEGORY_COLORS: Record<VowCategory, string> = {
  "refuge-commitments": "bg-vajra-blue/15 text-vajra-light-blue border-vajra-blue/30",
  "ten-virtues": "bg-vajra-cyan/15 text-vajra-cyan border-vajra-cyan/30",
  "bodhisattva-root": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "bodhisattva-secondary": "bg-purple-500/15 text-purple-400 border-purple-500/30",
  "bodhisattva-helping": "bg-pink-500/15 text-pink-400 border-pink-500/30",
  "bodhisattva-enlightenment": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  "bodhisattva-black-white": "bg-slate-500/15 text-slate-400 border-slate-500/30",
  "lay-vows": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

function getCategoryLabel(category: VowCategory): string {
  return VOW_CATEGORIES.find((c) => c.id === category)?.label ?? category;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, title, children }: SectionProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        {icon}
        {title}
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed pl-6">
        {children}
      </div>
    </div>
  );
}

export function VowDetailDialog({
  vow,
  open,
  onOpenChange,
  isFavorite,
  onToggleFavorite,
}: VowDetailDialogProps) {
  if (!vow) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[90dvh] rounded-t-2xl border-t border-gray-200 dark:border-[#1E3A5F] p-0 bg-white dark:bg-[#0D1117] [&>button]:hidden"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-[#1E3A5F]" />
        </div>

        <ScrollArea className="h-[calc(90dvh-1.5rem)]">
          <div className="px-5 pb-10 pt-2 space-y-5">
            {/* Header */}
            <SheetHeader className="space-y-3 text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-vajra-blue to-vajra-cyan text-sm font-bold text-white shadow-md">
                    {vow.id}
                  </span>
                  <div className="min-w-0">
                    <SheetTitle className="text-base leading-snug text-left">
                      {vow.de.title}
                    </SheetTitle>
                    {vow.titleSanskrit && (
                      <p className="text-xs text-muted-foreground/60 italic mt-0.5">
                        {vow.titleSanskrit}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => onToggleFavorite(vow.id)}
                    aria-label={isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
                  >
                    <Heart
                      className={cn(
                        "h-5 w-5 transition-colors",
                        isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground"
                      )}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground"
                    onClick={() => onOpenChange(false)}
                    aria-label="Schließen"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Badge
                variant="outline"
                className={cn("w-fit text-xs", CATEGORY_COLORS[vow.category])}
              >
                {getCategoryLabel(vow.category)}
              </Badge>

              <SheetDescription className="text-sm leading-relaxed text-left">
                {vow.de.description}
              </SheetDescription>
            </SheetHeader>

            <Separator className="bg-gray-100 dark:bg-[#1E3A5F]" />

            {/* Positive Formulierung */}
            {vow.de.positiveFormulation && (
              <Section
                icon={<Sparkles className="h-4 w-4 text-vajra-cyan" />}
                title="Positive Formulierung"
              >
                <p className="rounded-xl bg-vajra-cyan/5 border border-vajra-cyan/20 p-3 text-vajra-light-cyan">
                  {vow.de.positiveFormulation}
                </p>
              </Section>
            )}

            {/* Bedingungen */}
            {vow.de.conditions && (
              <Section
                icon={<AlertTriangle className="h-4 w-4 text-amber-400" />}
                title="Bedingungen (wann gilt es als gebrochen)"
              >
                <p>{vow.de.conditions}</p>
              </Section>
            )}

            {/* Fragen zum Nachdenken */}
            {vow.de.reflectionQuestions && vow.de.reflectionQuestions.length > 0 && (
              <Section
                icon={<HelpCircle className="h-4 w-4 text-vajra-blue" />}
                title="Fragen zum Nachdenken"
              >
                <ol className="list-[lower-alpha] list-inside space-y-1.5">
                  {vow.de.reflectionQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ol>
              </Section>
            )}

            {/* Alltagsbeispiele */}
            {vow.de.everydayExamples && vow.de.everydayExamples.length > 0 && (
              <Section
                icon={<CircleDot className="h-4 w-4 text-vajra-light-blue" />}
                title="Konzentrische Kreise (Alltagsbeispiele)"
              >
                <ul className="list-disc list-inside space-y-1">
                  {vow.de.everydayExamples.map((ex, i) => (
                    <li key={i}>{ex}</li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Ausnahmen */}
            {vow.de.exceptions && (
              <Section
                icon={<Target className="h-4 w-4 text-muted-foreground" />}
                title="Ausnahmen"
              >
                <p>{vow.de.exceptions}</p>
              </Section>
            )}

            {/* Karmische Korrelation */}
            {vow.de.karmicCorrelation && (
              <Section
                icon={<Scale className="h-4 w-4 text-purple-400" />}
                title="Karmische Korrelation"
              >
                <p className="rounded-xl bg-purple-500/5 border border-purple-500/20 p-3 text-purple-300">
                  {vow.de.karmicCorrelation}
                </p>
              </Section>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
